import type {GetServerSideProps} from 'next';
import type {GetServerSidePropsResult} from 'next/types';
import {getGroupedSupportCompanies} from '../../core/repositories/companyRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../core/repositories/usersMeRepository';
import {validateCookie} from '../../core/usecases/auth/checkOauthTokenExistence';
import {Page} from '../../features/search/components/SearchPage';
import {withHandler} from '../../server/token/withHandler';
import {ScriptForGA} from '../../shared/components/ScriptForGA';
import type {NextPageWithLayout} from '../_app';

type Props = {
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  companyNameList?: string[];
};

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession'
>(
  {auth: 'optional'},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {cookies} = context.req;

    const groupedSupportCompaniesResponse = await getGroupedSupportCompanies({
      queryParams: {},
    });
    if (!groupedSupportCompaniesResponse.ok) {
      return {
        props: {},
      };
    }
    const companyNameList = groupedSupportCompaniesResponse.ok
      ? groupedSupportCompaniesResponse.value.companyGroups.flatMap((group) =>
          group.companies.map((company) => company.name),
        )
      : [];

    const {isValid} = validateCookie(cookies);
    if (!isValid) {
      return {
        props: {
          companyNameList,
        },
      };
    }

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props: {
          companyNameList,
        },
      };
    }

    return {
      props: {
        userDataForAnalytics: userDataResponse.value,
        companyNameList,
      },
    };
  },
);

const SearchPage: NextPageWithLayout<Props> = ({
  userDataForAnalytics,
  companyNameList,
}) => {
  return (
    <>
      <Page companyNameList={companyNameList} />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

export default SearchPage;

SearchPage.getLayout = (page) => <>{page}</>;
